"use client";

import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";

type Preference = "system" | "light" | "dark";
type Scheme = "light" | "dark";
type Theme = { preference: Preference; resolved: Scheme; setPreference: (value: Preference) => void };
const ThemeContext = createContext<Theme | null>(null);
const key = "axiom-theme";
const query = "(prefers-color-scheme: dark)";
let preference: Preference | undefined;
const listeners = new Set<() => void>();

function readPreference(): Preference {
  try {
    const stored = localStorage.getItem(key);
    if (stored === "light" || stored === "dark") return stored;
  } catch { /* Storage may be disabled; System remains usable. */ }
  return "system";
}

function applyPreference(value: Preference) {
  const style = document.createElement("style");
  style.textContent = "*, *::before, *::after { transition: none !important; }";
  document.head.append(style);
  if (value === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = value;
  // Flush the new colours with transitions disabled before removing the guard.
  void window.getComputedStyle(document.documentElement).color;
  requestAnimationFrame(() => style.remove());
}

function emit() { listeners.forEach(listener => listener()); }
function setPreference(value: Preference) {
  preference = value;
  try { localStorage.setItem(key, value); } catch { /* Keep the in-memory choice. */ }
  applyPreference(value);
  emit();
}
function snapshot() {
  const value = preference ?? readPreference();
  const resolved = value === "system" ? (window.matchMedia(query).matches ? "dark" : "light") : value;
  return `${value}:${resolved}`;
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  const media = window.matchMedia(query);
  const onScheme = () => {
    if ((preference ?? readPreference()) === "system") applyPreference("system");
    emit();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== key && event.key !== null) return;
    preference = readPreference();
    applyPreference(preference);
    emit();
  };
  media.addEventListener("change", onScheme);
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    media.removeEventListener("change", onScheme);
    window.removeEventListener("storage", onStorage);
  };
}
const serverSnapshot = () => "system:light";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const current = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  const [chosen, scheme] = current.split(":");
  const value: Theme = {
    preference: chosen === "light" || chosen === "dark" ? chosen : "system",
    resolved: scheme === "dark" ? "dark" : "light",
    setPreference,
  };
  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme() {
  const theme = useContext(ThemeContext);
  if (!theme) throw new Error("useTheme requires ThemeProvider");
  return theme;
}
